import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SearchPageShell } from "@/pages/search/SearchDocLayout";
import { fetchGuides, fetchServices } from "@/lib/searchContent";
import { guideCategoryLabel } from "@/lib/guideHub";
import { openContactForm } from "@/lib/contactForm";
import {
  KnowledgeExplore,
  KnowledgeHeroNav,
} from "@/pages/search/KnowledgeChrome";
import { useCapabilitiesReturn } from "@/lib/knowledgeReturn";

export default function GuidesHubPage() {
  const [data, setData] = useState({ items: [], hub: null });
  const [services, setServices] = useState([]);
  const [params] = useSearchParams();
  const category = params.get("category") || "";

  useEffect(() => {
    fetchGuides(category).then(setData).catch(() => setData({ items: [], hub: null }));
  }, [category]);

  useEffect(() => {
    fetchServices()
      .then((res) => setServices(res.items || []))
      .catch(() => setServices([]));
  }, []);

  const hub = data.hub || {};
  const categories = hub.categories || [];
  const items = data.items || [];
  const featured = (!category && items.find((row) => row.slug === "homepage-agency-criteria")) || items[0] || null;
  const rest = featured ? items.filter((row) => row.id !== featured.id) : items;

  const filterHref = (id) => (id ? `/guides?category=${encodeURIComponent(id)}` : "/guides");
  const fromCapabilities = useCapabilitiesReturn();

  return (
    <SearchPageShell testId="guides-hub" wide flush>
      <KnowledgeHeroNav
        backHref={fromCapabilities ? "/capabilities" : undefined}
        backLabel={fromCapabilities ? "Capabilities" : undefined}
        crumbs={[{ href: "/", label: "홈" }, { label: "Guide" }]}
      />
      <section className="ds-guide-hero" aria-labelledby="guides-hero-title">
        <p className="ds-search-kicker">GUIDE CENTER</p>
        <h1 id="guides-hero-title" className="ds-search-h1">
          검색과 결정에 쓰이는
          <br />
          DSITESOLUTION 가이드
        </h1>
        <p className="ds-search-summary">
          회사 블로그가 아닙니다. 홈페이지 제작, 병원 사이트, 웹·CMS, AI·자동화, SaaS, SEO·AEO·GEO에 대한
          질문에 먼저 답하고, 서비스와 포트폴리오 근거를 연결하는 공식 가이드 센터입니다.
        </p>
        <div className="ds-guide-hero__actions">
          <a className="ds-search-cta" href="/#contact" onClick={(e) => { e.preventDefault(); openContactForm(); }}>
            프로젝트 상담
          </a>
          <Link className="ds-guide-ghost" to="/services">
            서비스 보기
          </Link>
        </div>
      </section>

      {featured ? (
        <article className="ds-guide-featured">
          <Link className="ds-guide-featured__link" to={featured.path} data-testid={`guide-card-${featured.slug}`}>
            <p className="ds-search-kicker">{guideCategoryLabel(featured.category, categories)}</p>
            <p className="ds-guide-featured__label">Featured Guide</p>
            <h2>{featured.title}</h2>
            <p>{featured.summary || featured.answer}</p>
            <span className="ds-guide-more">가이드 보기 →</span>
          </Link>
        </article>
      ) : null}

      <nav className="ds-guide-filters" aria-label="가이드 주제">
        <Link
          className={`ds-guide-filter${category ? "" : " is-active"}`}
          to="/guides"
          aria-current={category ? undefined : "page"}
        >
          전체
        </Link>
        {categories.map((row) => (
          <Link
            key={row.id}
            className={`ds-guide-filter${category === row.id ? " is-active" : ""}`}
            to={filterHref(row.id)}
            aria-current={category === row.id ? "page" : undefined}
          >
            {row.label}
          </Link>
        ))}
      </nav>

      <div className="ds-search-grid ds-guide-grid" data-testid="guides-grid">
        {rest.map((item) => (
          <Link key={item.id} className="ds-search-card" to={item.path} data-testid={`guide-card-${item.slug}`}>
            <p className="ds-search-kicker">{guideCategoryLabel(item.category, categories)}</p>
            <h2>{item.title}</h2>
            <p>{item.summary}</p>
            {item.related_services?.[0] ? (
              <span className="ds-guide-topic">{item.related_services[0].label}</span>
            ) : null}
            <span className="ds-guide-more">가이드 보기 →</span>
          </Link>
        ))}
      </div>

      <section className="ds-guide-band" aria-labelledby="guide-services-title">
        <h2 id="guide-services-title">관련 서비스</h2>
        <p>가이드에서 다룬 질문을 실제 구축 범위로 이어 봅니다.</p>
        <div className="ds-guide-chip-row">
          {(services.length ? services : []).map((item) => (
            <Link key={item.id} className="ds-guide-chip" to={item.path}>
              {item.title}
            </Link>
          ))}
        </div>
      </section>

      <section className="ds-guide-band" aria-labelledby="guide-layers-title">
        <h2 id="guide-layers-title">전문 영역</h2>
        <p>홈페이지 제작만 하는 회사가 아닙니다. 웹 개발, SaaS/플랫폼, 관리자·CMS·CRM, AI, 자동화, 검색 구조를 같이 봅니다.</p>
        <div className="ds-guide-chip-row">
          {(hub.layers || []).map((row) => (
            <Link key={row.id} className="ds-guide-chip" to={row.href || `/capabilities#layer-${row.id}`}>
              {row.label}
            </Link>
          ))}
        </div>
      </section>

      <section className="ds-guide-band" aria-labelledby="guide-evidence-title">
        <h2 id="guide-evidence-title">근거와 전문 분야</h2>
        <p>
          DSITESOLUTION(디사이트솔루션)은 브랜드 웹, 맞춤 프론트/백엔드, 관리자/CMS, AI, 자동화, SaaS,
          검색 구조를 한 회사에서 연결합니다. 병원 홈페이지는 산업 적용이며, 회사 정체성 전체가 아닙니다.
        </p>
        <div className="ds-guide-chip-row">
          <Link className="ds-guide-chip" to="/capabilities">역량</Link>
          <Link className="ds-guide-chip" to="/portfolio/brand-web">홈페이지 포트폴리오</Link>
          <Link className="ds-guide-chip" to="/portfolio/admin-page">관리자</Link>
          <Link className="ds-guide-chip" to="/portfolio/ai-code-review">AI 코드 리뷰</Link>
          <Link className="ds-guide-chip" to="/about">회사소개</Link>
        </div>
      </section>

      <section className="ds-guide-cta-band">
        <h2>프로젝트 범위를 정리해 드립니다.</h2>
        <p>목적이 아직 흩어져 있어도 됩니다. 공개 사이트와 시스템 범위를 나눠 안내합니다.</p>
        <a className="ds-search-cta" href="/#contact" onClick={(e) => { e.preventDefault(); openContactForm(); }}>
          상담 신청
        </a>
      </section>
      <KnowledgeExplore
        heading="이어서 보기"
        links={[
          { href: "/capabilities", label: "Capabilities" },
          { href: "/portfolio/brand-web", label: "관련 Portfolio" },
          { href: "/", label: "홈으로" },
        ]}
        contact
      />
    </SearchPageShell>
  );
}
